/**
 * Condition-Specific Content Modules
 *
 * Static content registry mapping conditions to step-specific
 * education overlays. Injected when user has reported conditions.
 */

import type { ConditionKey } from "../plan.types";

export type ConditionStepContent = {
  condition: ConditionKey;
  title: string;
  stepId: string;
  education: string;
  details?: string;
  supplementRecommendations?: {
    name: string;
    dosage: string;
    evidence: "strong" | "moderate" | "emerging";
    note: string;
  }[];
};

/**
 * PMOS (ex PCOS) content across steps
 */
const PCOS_CONTENT: ConditionStepContent[] = [
  {
    condition: "pcos",
    title: "PMOS (ex PCOS) & Your Journey",
    stepId: "learn",
    education:
      "PMOS (ex PCOS) affects how your ovaries respond to stimulation. You may produce more eggs than average, but there\u2019s a higher risk of ovarian hyperstimulation syndrome (OHSS). Your clinic will likely use a conservative protocol with close monitoring.",
    details:
      "What this means for treatment:\n\u2022 Higher antral follicle count (AFC) is common\n\u2022 Your clinic may use lower medication doses\n\u2022 Lupron trigger instead of hCG may be recommended\n\u2022 Freeze-all cycles are more common to reduce OHSS risk",
  },
  {
    condition: "pcos",
    title: "PMOS (ex PCOS) Supplements",
    stepId: "prep",
    education:
      "Supplements with evidence for PMOS (ex PCOS) can improve egg quality and insulin sensitivity. Starting these 2\u20133 months before treatment is ideal.",
    supplementRecommendations: [
      {
        name: "Myo-Inositol + D-Chiro-Inositol",
        dosage: "4g myo + 100mg d-chiro daily",
        evidence: "strong",
        note: "Improves insulin sensitivity and egg quality in PMOS (ex PCOS)",
      },
      {
        name: "CoQ10",
        dosage: "400\u2013600mg daily",
        evidence: "moderate",
        note: "Supports mitochondrial function in oocytes",
      },
      {
        name: "Vitamin D",
        dosage: "2000\u20134000 IU daily",
        evidence: "moderate",
        note: "Many with PMOS (ex PCOS) are deficient; supports hormonal balance",
      },
      {
        name: "Omega-3 Fatty Acids",
        dosage: "1\u20132g EPA+DHA daily",
        evidence: "moderate",
        note: "Reduces inflammation and supports egg quality",
      },
    ],
  },
  {
    condition: "pcos",
    title: "PMOS (ex PCOS) During Stimulation",
    stepId: "treatment",
    education:
      "With PMOS (ex PCOS), your estradiol may rise faster than typical. Your clinic adjusts doses based on this. If OHSS risk is high, they may: reduce doses, coast (stop meds for a day), use Lupron trigger instead of hCG, or recommend freezing all embryos (no fresh transfer).",
    details:
      "Signs of OHSS to watch for:\n\u2022 Rapid weight gain (>2 lbs/day)\n\u2022 Severe bloating or abdominal pain\n\u2022 Nausea or vomiting\n\u2022 Decreased urination\n\u2022 Shortness of breath\n\nMild OHSS (bloating, discomfort) is common and resolves on its own. Moderate to severe OHSS requires medical attention.",
  },
];

/**
 * Endometriosis content across steps
 */
const ENDO_CONTENT: ConditionStepContent[] = [
  {
    condition: "endometriosis",
    title: "Endometriosis & Fertility",
    stepId: "learn",
    education:
      "Endometriosis can affect egg quality and ovarian reserve, especially if you have endometriomas (chocolate cysts). Surgery before treatment is sometimes recommended but debated \u2014 discuss with your RE.",
    details:
      "Key considerations:\n\u2022 Endometriomas >4cm may need surgical drainage before retrieval\n\u2022 Surgery can reduce ovarian reserve (lower AMH after)\n\u2022 Pain doesn\u2019t correlate with severity \u2014 mild pain can coexist with significant disease\n\u2022 Suppression before stimulation may improve outcomes",
  },
  {
    condition: "endometriosis",
    title: "Interpreting Your Results with Endo",
    stepId: "test",
    education:
      "Your AMH may be affected by endometriosis, especially after ovarian surgery. Your clinic will interpret your results in context. A lower AMH doesn\u2019t necessarily mean fewer eggs \u2014 it means your protocol may need adjustment.",
  },
  {
    condition: "endometriosis",
    title: "Endo Treatment Protocol",
    stepId: "treatment",
    education:
      "Endometriosis protocols sometimes include a longer suppression phase (2\u20133 months on birth control or Lupron) before stimulation to reduce inflammation. This \u201Cquiet\u201D period can improve egg quality.",
    details:
      "Your clinic may recommend:\n\u2022 Extended suppression (2\u20133 months) before stimulation\n\u2022 Higher gonadotropin doses if reserve is diminished\n\u2022 Freeze-all with delayed transfer for better endometrial receptivity\n\u2022 Excision surgery before IVF if deep infiltrating endometriosis is present",
  },
];

/**
 * Thyroid content
 */
const THYROID_CONTENT: ConditionStepContent[] = [
  {
    condition: "thyroid",
    title: "Thyroid & Fertility",
    stepId: "prep",
    education:
      "Thyroid function directly affects fertility and pregnancy. TSH should ideally be between 1.0\u20132.5 mIU/L for conception. If yours is out of range, your RE may adjust medication before starting a cycle.",
    details:
      "Why thyroid matters:\n\u2022 Hypothyroidism can impair ovulation and implantation\n\u2022 Hyperthyroidism increases miscarriage risk\n\u2022 TSH is checked as part of baseline bloodwork\n\u2022 If you\u2019re already on thyroid medication, your dose may change during treatment\n\u2022 Thyroid antibodies (TPO) are also worth testing \u2014 even with normal TSH, antibodies can affect outcomes",
  },
  {
    condition: "thyroid",
    title: "Monitoring During Treatment",
    stepId: "treatment",
    education:
      "Your thyroid levels will be monitored during stimulation, as estrogen changes can affect thyroid function. If you\u2019re on levothyroxine, your dose may need temporary adjustment.",
  },
];

/**
 * Male factor content
 */
const MALE_FACTOR_CONTENT: ConditionStepContent[] = [
  {
    condition: "male-factor",
    title: "Understanding Male Factor",
    stepId: "test",
    education:
      "If semen analysis shows low parameters, your clinic may recommend: repeat SA (results vary significantly between samples), lifestyle changes for 2\u20133 months, or direct to ICSI for fertilization.",
    details:
      "Key SA parameters:\n\u2022 Count: >15 million/mL is normal\n\u2022 Motility: >40% total, >32% progressive\n\u2022 Morphology: >4% normal forms (strict criteria)\n\u2022 Volume: 1.5\u20135.0 mL\n\nOne abnormal SA doesn\u2019t define fertility. Illness, stress, heat exposure, and timing can all affect results. A repeat test 2\u20133 months later is standard.",
  },
  {
    condition: "male-factor",
    title: "ICSI & Male Factor",
    stepId: "treatment",
    education:
      "With male factor, ICSI (intracytoplasmic sperm injection) is typically used instead of conventional insemination. A single sperm is injected directly into each mature egg, bypassing motility and morphology barriers.",
  },
];

/**
 * Recurrent loss content
 */
const RECURRENT_LOSS_CONTENT: ConditionStepContent[] = [
  {
    condition: "recurrent-loss",
    title: "Recurrent Loss & Testing",
    stepId: "learn",
    education:
      "If you\u2019ve experienced pregnancy losses, your clinic may recommend additional testing: karyotyping (chromosomal analysis for both partners), uterine evaluation, thrombophilia panel, and immunological testing. PGT-A (genetic testing of embryos) is often recommended.",
    details:
      "Additional workup may include:\n\u2022 Karyotype for both partners (rule out balanced translocations)\n\u2022 Uterine evaluation (HSG, SIS, or hysteroscopy)\n\u2022 Thrombophilia panel (Factor V Leiden, MTHFR, antiphospholipid antibodies)\n\u2022 Immunological testing (NK cells, cytokines)\n\u2022 Endometrial biopsy / ERA testing for implantation window",
  },
  {
    condition: "recurrent-loss",
    title: "PGT-A & Recurrent Loss",
    stepId: "treatment",
    education:
      "PGT-A tests each embryo for chromosomal abnormalities before transfer. For recurrent loss, this can help identify which embryos have the best chance. It adds 1\u20132 weeks to the timeline (embryos are biopsied, frozen, results return, then a frozen transfer is planned).",
  },
];

/**
 * Diminished ovarian reserve content
 */
const DOR_CONTENT: ConditionStepContent[] = [
  {
    condition: "diminished-reserve",
    title: "Diminished Reserve & Your Plan",
    stepId: "test",
    education:
      "Lower AMH or high FSH indicates diminished ovarian reserve. This doesn\u2019t mean you can\u2019t succeed \u2014 it means your protocol will be tailored to maximize your response. Quality over quantity becomes the focus.",
    details:
      "What your clinic considers:\n\u2022 AMH <1.0 ng/mL or FSH >10 suggests diminished reserve\n\u2022 Antral follicle count (AFC) is equally important\n\u2022 Aggressive protocols (higher doses, microdose Lupron flare) may be used\n\u2022 Multiple cycles may be recommended to bank eggs/embryos\n\u2022 Time sensitivity is higher \u2014 your RE may recommend moving quickly",
  },
  {
    condition: "diminished-reserve",
    title: "DOR During Stimulation",
    stepId: "treatment",
    education:
      "With diminished reserve, your clinic may use higher medication doses and a shorter stimulation. Fewer eggs retrieved is expected, but egg quality can still be excellent. Every egg matters.",
  },
];

/**
 * Age 38+ content
 */
const AGE_38_CONTENT: ConditionStepContent[] = [
  {
    condition: "age-38-plus",
    title: "Age & Egg Quality",
    stepId: "learn",
    education:
      "After 38, the proportion of chromosomally normal eggs decreases. This is the single biggest factor in fertility outcomes. The good news: treatments like PGT-A can help identify the best embryos, and many people in this age group have successful outcomes.",
    details:
      "Key stats:\n\u2022 At 38, roughly 50\u201360% of eggs are chromosomally normal\n\u2022 At 40, roughly 30\u201340%\n\u2022 At 42, roughly 15\u201325%\n\u2022 These are averages \u2014 individual variation is significant\n\u2022 AMH and AFC tell you about quantity, not quality\n\u2022 PGT-A helps select the best embryo for transfer",
  },
  {
    condition: "age-38-plus",
    title: "Time-Sensitive Decisions",
    stepId: "test",
    education:
      "At 38+, time is a meaningful factor. Your clinic may recommend proceeding to treatment sooner rather than optimizing for months. The balance shifts toward action \u2014 every 6 months matters more than at younger ages.",
  },
];

/**
 * Master registry: all condition content indexed by condition key
 */
export const CONDITION_CONTENT_REGISTRY: Record<
  ConditionKey,
  ConditionStepContent[]
> = {
  pcos: PCOS_CONTENT,
  endometriosis: ENDO_CONTENT,
  thyroid: THYROID_CONTENT,
  "male-factor": MALE_FACTOR_CONTENT,
  "recurrent-loss": RECURRENT_LOSS_CONTENT,
  "diminished-reserve": DOR_CONTENT,
  "age-38-plus": AGE_38_CONTENT,
};

/**
 * Get condition content for a specific step
 */
export function getConditionContentForStep(
  conditions: ConditionKey[],
  stepId: string,
): ConditionStepContent[] {
  const results: ConditionStepContent[] = [];
  for (const condition of conditions) {
    const content = CONDITION_CONTENT_REGISTRY[condition];
    if (content) {
      const stepContent = content.filter((c) => c.stepId === stepId);
      results.push(...stepContent);
    }
  }
  return results;
}
